import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { ConfigService } from '../../shared/services/config.service';
import { LocationService } from '../../utilities/services/location.service';
import {
  AuthState,
  LoginRequest,
  LoginResponse,
  SendCodeRequest,
  SendCodeResponse,
  SetPasswordRequest,
  SetPasswordResponse,
  User,
  ValidateCodeRequest,
  ValidateCodeResponse,
  VerifyDocumentRequest,
  VerifyDocumentResponse,
  VerifyTokenResponse,
} from '../interfaces/auth.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private URL_API() {
    return (
      this.locationService.getApiUrl() || this.configService.getApiBaseUrl()
    );
  }

  // Estado de autenticación con signals
  private authState = signal<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
  });

  // Observable para componentes que necesiten suscribirse
  private authSubject = new BehaviorSubject<AuthState>(this.authState());

  constructor(
    private http: HttpClient,
    private locationService: LocationService,
    private configService: ConfigService,
    private router: Router,
    private toastr: ToastrService
  ) {
    this.initializeAuth();
  }

  // Getters públicos
  get currentUser() {
    return this.authState().user;
  }

  get isAuthenticated() {
    return this.authState().isAuthenticated;
  }

  get isLoading() {
    return this.authState().isLoading;
  }

  get authState$() {
    return this.authSubject.asObservable();
  }

  get authStateSignal() {
    return this.authState.asReadonly();
  }

  private initializeAuth(): void {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');

    if (token && userData) {
      try {
        const user: User = JSON.parse(userData);
        this.updateAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });

        // Verificar si el token sigue siendo válido
        this.verifyToken().subscribe({
          next: (response) => {
            if (response.error) {
              this.handleSessionExpired(response.error);
            }
          },
          error: (err) => {
            this.handleSessionExpired(err.error?.error);
          },
        });
      } catch (error) {
        this.logout();
      }
    }
  }

  private updateAuthState(newState: Partial<AuthState>): void {
    const currentState = this.authState();
    const updatedState = { ...currentState, ...newState };
    this.authState.set(updatedState);
    this.authSubject.next(updatedState);
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  verifyDocument(documentNumber: string): Observable<VerifyDocumentResponse> {
    this.updateAuthState({ isLoading: true, error: null });

    const request: VerifyDocumentRequest = { documentNumber };

    return this.http
      .post<VerifyDocumentResponse>(
        `${this.URL_API()}/auth/clients/verifyDocument.php`,
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        tap(() => this.updateAuthState({ isLoading: false })),
        catchError((error) => {
          this.updateAuthState({
            isLoading: false,
            error: error.error?.message || 'Error verificando documento',
          });
          return throwError(() => error);
        })
      );
  }

  sendCode(
    clientId: string,
    method: 'phone' | 'email' | 'whatsapp'
  ): Observable<SendCodeResponse> {
    this.updateAuthState({ isLoading: true, error: null });

    const request: SendCodeRequest = { clientId, method };

    return this.http
      .post<SendCodeResponse>(
        `${this.URL_API()}/auth/clients/sendCode.php`,
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        tap(() => this.updateAuthState({ isLoading: false })),
        catchError((error) => {
          this.updateAuthState({
            isLoading: false,
            error: error.error?.message || 'Error enviando código',
          });
          return throwError(() => error);
        })
      );
  }

  validateCode(
    clientId: string,
    code: string
  ): Observable<ValidateCodeResponse> {
    this.updateAuthState({ isLoading: true, error: null });

    const request: ValidateCodeRequest = { clientId, code };

    return this.http
      .post<ValidateCodeResponse>(
        `${this.URL_API()}/auth/clients/validateCode.php`,
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        tap(() => this.updateAuthState({ isLoading: false })),
        catchError((error) => {
          this.updateAuthState({
            isLoading: false,
            error: error.error?.message || 'Código inválido',
          });
          return throwError(() => error);
        })
      );
  }

  setPassword(
    clientId: string,
    password: string
  ): Observable<SetPasswordResponse> {
    this.updateAuthState({ isLoading: true, error: null });

    const request: SetPasswordRequest = {
      clientId,
      password: password,
    };

    return this.http
      .post<SetPasswordResponse>(
        `${this.URL_API()}/auth/clients/setPassword.php`,
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        tap(() => this.updateAuthState({ isLoading: false })),
        catchError((error) => {
          this.updateAuthState({
            isLoading: false,
            error: error.error?.message || 'Error estableciendo contraseña',
          });
          return throwError(() => error);
        })
      );
  }

  login(
    documentType: string,
    documentNumber: string,
    password: string
  ): Observable<LoginResponse> {
    this.updateAuthState({ isLoading: true, error: null });

    const request: LoginRequest = {
      documentType,
      documentNumber,
      password: btoa(password), // Encodificar en base64
    };

    return this.http
      .post<LoginResponse>(
        `${this.URL_API()}/auth/clients/login.php`,
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        tap((response) => {
          if (response.status === 200 && response.data) {
            const user: User = {
              ...response.data,
              documentNumber,
            };

            // Guardar en localStorage
            localStorage.setItem('authToken', response.data.token);
            localStorage.setItem('userData', JSON.stringify(user));

            this.updateAuthState({
              user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });

            // Redirigir si hay una URL de retorno
            const returnUrl =
              this.router.routerState.snapshot.root.queryParams['returnUrl'] ||
              '/';
            this.router.navigateByUrl(returnUrl);
          }
        }),
        catchError((error) => {
          this.updateAuthState({
            isLoading: false,
            error: error.error?.message || 'Error en el inicio de sesión',
          });
          return throwError(() => error);
        })
      );
  }

  verifyToken(): Observable<VerifyTokenResponse> {
    return this.http
      .get<VerifyTokenResponse>(
        `${this.URL_API()}/auth/clients/verifyToken.php`,
        { headers: this.getAuthHeaders() }
      )
      .pipe(
        catchError((error) => {
          this.handleSessionExpired(error.error?.error);
          return throwError(() => error);
        })
      );
  }

  logout(sessionExpired: boolean = false): void {
    if (sessionExpired) {
      const returnUrl = this.router.url;
      this.router.navigate(['/auth/login'], { queryParams: { returnUrl } });
    } else {
      this.router.navigate(['/']);
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');

    this.updateAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }

  clearError(): void {
    this.updateAuthState({ error: null });
  }

  private handleSessionExpired(message?: string): void {
    this.toastr.warning(
      message || 'Su sesión ha expirado. Por favor, inicie sesión de nuevo.',
      'Sesión Expirada'
    );
    this.logout(true);
  }
}
